import React, { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { personalLandingOgShareUrl } from '../lib/publicShareUrls'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { initKakao } from '../lib/kakao'
import { resizeForProfileUpload } from '../lib/imageResize'
import PersonalLandingSectionsEditor from '../features/publicPage/PersonalLandingSectionsEditor'
import PersonalLandingDraftPreview from '../components/PersonalLandingDraftPreview'
import PersonalLandingPreviewViewport from '../components/PersonalLandingPreviewViewport'
import PersonalLandingSaveReviewModal from '../components/PersonalLandingSaveReviewModal'
import { collectMarketingStrings } from '../lib/collectMarketingStrings'
import EasyReconfigureModal, { EasyReconfigureContent } from '../features/reconfigure/EasyReconfigureModal'
import {
  LANDING_SECTION_OPTIONS,
  LANDING_SKIN_OPTIONS,
  serializeLandingSections,
} from '../features/publicPage/registry'
import {
  PERSONAL_LANDING_RECONFIGURE_FEATURES,
  PERSONAL_LANDING_RECONFIGURE_PURPOSES,
  buildPersonalLandingReconfigureSections,
  getDefaultPersonalLandingFeatureValues,
  getPersonalLandingPurposeFromSections,
} from '../features/reconfigure/reconfigurePresets'
import { isPersonalLandingConfigured } from '../features/reconfigure/setupStatus'
import { normalizePersonalLandingSections } from '../features/personalLanding/normalize'
import MenuGuideButton from '../components/MenuGuideButton'
import { EditorStatePage } from '../ui/PageLayout'

const PERSONAL_SECTION_WRITE_DESCRIPTIONS = {
  hero: '첫 화면 제목과 소개 문구를 정리합니다.',
  'about-profile': '상담 스타일과 강점을 적습니다.',
  'service-tags': '도움 줄 수 있는 분야를 짧게 보여줍니다.',
  'proof-stats': '성과나 상담 건수를 숫자로 보여줍니다.',
  'monthly-issue': '카드뉴스나 최근 이슈를 연결합니다.',
  'media-gallery': '사진으로 활동과 분위기를 보여줍니다.',
  'image-card': '한 장의 이미지와 메시지를 넣습니다.',
  career: '경력과 이력을 정리합니다.',
  'customer-case': '고객이 이해하기 쉬운 사례를 적습니다.',
  activity: '활동지역과 상담 가능 범위를 입력합니다.',
  'free-text': '하고 싶은 말을 자유롭게 담습니다.',
  contact: '주소와 상담 링크를 연결합니다.',
  cta: '상담 신청을 유도하는 문구를 정리합니다.',
  credentials: '자격과 교육 이력을 입력합니다.',
  faq: '자주 묻는 질문을 정리합니다.',
  'ad-review': '심의필과 준법 문구를 입력합니다.',
}

function getPersonalSectionLabel(type) {
  return LANDING_SECTION_OPTIONS.find((item) => item.type === type)?.label || '섹션'
}

function getPersonalWritingItems(sections = []) {
  return (sections || []).map((section, index) => ({
    id: section?.id || `${section?.type || 'section'}-${index}`,
    label: getPersonalSectionLabel(section?.type),
    description: PERSONAL_SECTION_WRITE_DESCRIPTIONS[section?.type] || '문구와 표시 내용을 정리합니다.',
  }))
}

export default function MyPublicLandingPage() {
  const navigate = useNavigate()

  const myQuery = useQuery({
    queryKey: ['me', 'public-page'],
    queryFn: async () => {
      const res = await api.get('/users/me/public-page', { __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    }
  })

  const d = myQuery.data
  const origin = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : ''
  const code = d?.code || ''
  const ogVersion = d?.ogVersion || d?.updatedAt || ''
  const landingUrlPath = code ? `/p/${code}` : ''
  const landingSharePath = code ? `/v1/share/landing/${code}` : ''
  const absoluteLandingUrl = origin && landingUrlPath ? `${origin}${landingUrlPath}` : landingUrlPath
  const absoluteShareUrl = personalLandingOgShareUrl(code, ogVersion) || (origin && landingSharePath ? `${origin}${landingSharePath}` : landingSharePath)

  const [showLandingLink, setShowLandingLink] = useState(true)
  const [bioShort, setBioShort] = useState('')
  const [introText, setIntroText] = useState('')
  const [careerText, setCareerText] = useState('')
  const [activityAreaText, setActivityAreaText] = useState('')
  const [freeText, setFreeText] = useState('')
  const [kakaoChatUrl, setKakaoChatUrl] = useState('')
  const [profileImageUrl, setProfileImageUrl] = useState('')
  const [profileImageFileId, setProfileImageFileId] = useState('')
  const [showCareerCard, setShowCareerCard] = useState(true)
  const [showActivityAreaCard, setShowActivityAreaCard] = useState(true)
  const [showFreeTextCard, setShowFreeTextCard] = useState(false)
  const [imageCardEnabled, setImageCardEnabled] = useState(false)
  const [imageCardUrl, setImageCardUrl] = useState('')
  const [imageCardFileId, setImageCardFileId] = useState('')
  const [landingGalleryEnabled, setLandingGalleryEnabled] = useState(false)
  const [landingGalleryItems, setLandingGalleryItems] = useState([])
  const [companyNameOverride, setCompanyNameOverride] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [addressText, setAddressText] = useState('')
  const [mobileFaxNumber, setMobileFaxNumber] = useState('')
  const [serviceTags, setServiceTags] = useState('')
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [instagramUrl, setInstagramUrl] = useState('')
  const [blogUrl, setBlogUrl] = useState('')
  const [landingSections, setLandingSections] = useState([])
  const [expandedSectionId, setExpandedSectionId] = useState('')
  const [landingTemplateId, setLandingTemplateId] = useState('LANDING_V1')
  const [landingPageStyle, setLandingPageStyle] = useState({})
  const [saveReviewOpen, setSaveReviewOpen] = useState(false)
  const [mobileEditorTab, setMobileEditorTab] = useState('edit')
  const [previewDeviceMode, setPreviewDeviceMode] = useState('pc')
  const [reconfigureOpen, setReconfigureOpen] = useState(false)
  const [reconfigurePurpose, setReconfigurePurpose] = useState('trust')
  const [reconfigureFeatures, setReconfigureFeatures] = useState(() => getDefaultPersonalLandingFeatureValues([], 'trust'))
  const [initialSetupDone, setInitialSetupDone] = useState(false)
  const [landingEditorMode, setLandingEditorMode] = useState('guided')
  const [guidedStart, setGuidedStart] = useState({ key: 0, phase: 1, index: 0 })

  const templatesQuery = useQuery({
    queryKey: ['user-landing-page-templates', 'active'],
    queryFn: async () => {
      const res = await api.get('/user-landing-page-templates', { params: { active: true }, __silent: true })
      const payload = res?.data?.data || res?.data
      return Array.isArray(payload?.items) ? payload.items : []
    },
  })

  React.useEffect(() => {
    if (!d) return
    setShowLandingLink(d.showLandingLink !== false)
    setBioShort(d.bioShort || '')
    setIntroText(d.introText || '')
    setCareerText(d.careerText || '')
    setActivityAreaText(d.activityAreaText || '')
    setFreeText(d.freeText || '')
    setKakaoChatUrl(d.kakaoChatUrl || '')
    setProfileImageUrl(d.profileImageUrl || '')
    setProfileImageFileId(d.profileImageFileId || '')
    setShowCareerCard(d.showCareerCard !== false)
    setShowActivityAreaCard(d.showActivityAreaCard !== false)
    setShowFreeTextCard(d.showFreeTextCard !== false)
    setImageCardEnabled(Boolean(d.imageCardEnabled))
    setImageCardUrl(d.imageCardUrl || '')
    setImageCardFileId(d.imageCardFileId || '')
    setLandingGalleryEnabled(Boolean(d.landingGalleryEnabled))
    setLandingGalleryItems(Array.isArray(d.landingGalleryItems) ? d.landingGalleryItems : [])
    setCompanyNameOverride(d.companyNameOverride || '')
    setJobTitle(d.jobTitle || '')
    setAddressText(d.addressText || '')
    setMobileFaxNumber(d.mobileFaxNumber || '')
    setServiceTags(d.serviceTags || '')
    setYoutubeUrl(d.youtubeUrl || '')
    setInstagramUrl(d.instagramUrl || '')
    setBlogUrl(d.blogUrl || '')
    setLandingSections(normalizePersonalLandingSections(d.landingSections, d, d.landingPageStyle))
    setLandingTemplateId(d.landingTemplateId || 'LANDING_V1')
    setLandingPageStyle(d.landingPageStyle && typeof d.landingPageStyle === 'object' ? { ...d.landingPageStyle } : {})
  }, [d])

  const updateMutation = useMutation({
    mutationFn: async (override) => {
      const body = {
        cardEnabled: true,
        landingEnabled: true,
        showLandingLink: override?.showLandingLink ?? showLandingLink,
        bioShort: override?.bioShort ?? bioShort,
        introText: override?.introText ?? introText,
        careerText: override?.careerText ?? careerText,
        activityAreaText: override?.activityAreaText ?? activityAreaText,
        freeText: override?.freeText ?? freeText,
        kakaoChatUrl: override?.kakaoChatUrl ?? kakaoChatUrl,
        profileImageFileId: override?.profileImageFileId ?? profileImageFileId,
        showCareerCard: override?.showCareerCard ?? showCareerCard,
        showActivityAreaCard: override?.showActivityAreaCard ?? showActivityAreaCard,
        showFreeTextCard: override?.showFreeTextCard ?? showFreeTextCard,
        imageCardEnabled: override?.imageCardEnabled ?? imageCardEnabled,
        imageCardFileId: override?.imageCardFileId ?? imageCardFileId,
        companyNameOverride: override?.companyNameOverride ?? companyNameOverride,
        jobTitle: override?.jobTitle ?? jobTitle,
        addressText: override?.addressText ?? addressText,
        mobileFaxNumber: override?.mobileFaxNumber ?? mobileFaxNumber,
        serviceTags: override?.serviceTags ?? serviceTags,
        landingSections: override?.landingSections ?? serializeLandingSections(landingSections),
        youtubeUrl: override?.youtubeUrl ?? youtubeUrl,
        instagramUrl: override?.instagramUrl ?? instagramUrl,
        blogUrl: override?.blogUrl ?? blogUrl,
        landingTemplateId: override?.landingTemplateId ?? landingTemplateId,
        landingPageStyle: override?.landingPageStyle ?? landingPageStyle,
      }
      // 소개페이지 메인 슬라이드(갤러리)는 변경 시에만 전송(불필요한 토큰 재발급 방지)
      if (override && Object.prototype.hasOwnProperty.call(override, 'landingGalleryEnabled')) {
        body.landingGalleryEnabled = override.landingGalleryEnabled
      }
      if (override && Object.prototype.hasOwnProperty.call(override, 'landingGalleryFileIds')) {
        body.landingGalleryFileIds = override.landingGalleryFileIds
      }
      const res = await api.put('/users/me/public-page', body)
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onSuccess: (updated, variables) => {
      try { window?.toast?.show?.('개인홈페이지 설정이 저장되었습니다', 'success') } catch { }
      myQuery.refetch()
      setShowLandingLink(updated?.showLandingLink !== false)
      setBioShort(updated?.bioShort || '')
      setIntroText(updated?.introText || '')
      setCareerText(updated?.careerText || '')
      setActivityAreaText(updated?.activityAreaText || '')
      setFreeText(updated?.freeText || '')
      setKakaoChatUrl(updated?.kakaoChatUrl || '')
      setProfileImageUrl(updated?.profileImageUrl || '')
      setProfileImageFileId(updated?.profileImageFileId || '')
      setShowCareerCard(updated?.showCareerCard !== false)
      setShowActivityAreaCard(updated?.showActivityAreaCard !== false)
      setShowFreeTextCard(updated?.showFreeTextCard !== false)
      setImageCardEnabled(Boolean(updated?.imageCardEnabled))
      setImageCardUrl(updated?.imageCardUrl || '')
      setImageCardFileId(updated?.imageCardFileId || '')
      setLandingGalleryEnabled(Boolean(updated?.landingGalleryEnabled))
      setLandingGalleryItems(Array.isArray(updated?.landingGalleryItems) ? updated.landingGalleryItems : [])
      setCompanyNameOverride(updated?.companyNameOverride || '')
      setJobTitle(updated?.jobTitle || '')
      setAddressText(updated?.addressText || '')
      setMobileFaxNumber(updated?.mobileFaxNumber || '')
      setServiceTags(updated?.serviceTags || '')
      setYoutubeUrl(updated?.youtubeUrl || '')
      setInstagramUrl(updated?.instagramUrl || '')
      setBlogUrl(updated?.blogUrl || '')
      setLandingSections(normalizePersonalLandingSections(updated?.landingSections, updated, updated?.landingPageStyle))
      setLandingPageStyle(updated?.landingPageStyle && typeof updated.landingPageStyle === 'object' ? { ...updated.landingPageStyle } : {})
      setLandingTemplateId(updated?.landingTemplateId || 'LANDING_V1')
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '저장 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  })

  const uploadProfileImageMutation = useMutation({
    mutationFn: async (file) => {
      if (!file) throw new Error('no_file')
      const resized = await resizeForProfileUpload(file, {
        maxSide: 1024,
        quality: 0.82,
        preferWebp: false,
        maxBytes: 3 * 1024 * 1024,
      })
      const fd = new FormData()
      fd.append('file', resized)
      const up = await api.post('/files/upload/public-profile-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const upData = (up?.data && up.data.data !== undefined) ? up.data.data : up.data
      const fileId = upData?.fileId
      if (!fileId) throw new Error('upload_failed')
      return { fileId }
    },
    onSuccess: ({ fileId }) => {
      const next = fileId || ''
      setProfileImageFileId(next)
      updateMutation.mutate({ __keepOpen: true, profileImageFileId: next })
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '이미지 업로드 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  })

  const uploadImageCardMutation = useMutation({
    mutationFn: async (file) => {
      if (!file) throw new Error('no_file')
      // 소개페이지 이미지카드는 좀 더 크게(긴변 1440) 유지
      const resized = await resizeForProfileUpload(file, {
        maxSide: 1440,
        quality: 0.84,
        preferWebp: true,
        maxBytes: 10 * 1024 * 1024,
      })
      const fd = new FormData()
      fd.append('file', resized)
      const up = await api.post('/files/upload/public-landing-image', fd)
      const upData = (up?.data && up.data.data !== undefined) ? up.data.data : up.data
      const fileId = upData?.fileId
      if (!fileId) throw new Error('upload_failed')
      return { fileId }
    },
    onSuccess: ({ fileId }) => {
      const next = fileId || ''
      setImageCardFileId(next)
      // 이미지 카드 사용 ON + fileId 저장
      updateMutation.mutate({ __keepOpen: true, imageCardEnabled: true, imageCardFileId: next })
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '이미지 업로드 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  })

  const uploadLandingGalleryMutation = useMutation({
    mutationFn: async (files) => {
      const list = Array.isArray(files) ? files : []
      const uploaded = []
      for (const file of list) {
        if (!file) continue
        // 메인 슬라이드 이미지는 용량을 조금 더 보수적으로 최적화
        const resized = await resizeForProfileUpload(file, {
          maxSide: 1280,
          quality: 0.82,
          preferWebp: true,
          maxBytes: 10 * 1024 * 1024,
        })
        const fd = new FormData()
        fd.append('file', resized)
        const up = await api.post('/files/upload/public-landing-image', fd)
        const upData = (up?.data && up.data.data !== undefined) ? up.data.data : up.data
        const fileId = upData?.fileId
        if (fileId) uploaded.push(fileId)
        if (uploaded.length >= 5) break
      }
      if (uploaded.length === 0) throw new Error('upload_failed')
      return uploaded
    },
    onSuccess: (newFileIds) => {
      const current = Array.isArray(landingGalleryItems) ? landingGalleryItems : []
      const curIds = current.map((it) => it?.fileId).filter(Boolean)
      const merged = [...curIds, ...newFileIds].slice(0, 5)
      updateMutation.mutate({ __keepOpen: true, landingGalleryEnabled: true, landingGalleryFileIds: merged })
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '이미지 업로드 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  })

  const smsShareHref = useMemo(() => {
    const name = d?.name || ''
    const isIOS = (typeof navigator !== 'undefined') ? /iPad|iPhone|iPod/.test(navigator.userAgent || '') : false
    const sep = isIOS ? '&' : '?'
    if (!(code && absoluteShareUrl && absoluteLandingUrl)) return ''
    return `sms:${sep}body=${encodeURIComponent(`${name} 소개페이지: ${absoluteShareUrl}`)}`
  }, [absoluteLandingUrl, absoluteShareUrl, code, d?.name])

  const copyToClipboard = useCallback(async (text, successMessage = '복사되었습니다.') => {
    const show = window?.toast?.show
    if (!text) {
      try { show?.('복사할 링크가 없습니다.', 'error') } catch { }
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      try { show?.(successMessage, 'success') } catch { }
    } catch {
      try { show?.('클립보드 복사에 실패했습니다.', 'error') } catch { }
    }
  }, [])

  /** 미리보기·링크·카카오·문자 — 헤더 툴바용 */
  const shareActionBtnClass =
    'inline-flex min-h-[36px] min-w-[4.5rem] flex-1 items-center justify-center whitespace-nowrap rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 sm:min-w-0 sm:flex-none sm:px-3 sm:text-sm'

  const dbTemplatesActive = useMemo(() => {
    const raw = templatesQuery.data
    if (!Array.isArray(raw)) return []
    return raw.filter((t) => t?.isActive !== false)
  }, [templatesQuery.data])

  const reconfigurePreviewSections = useMemo(() => {
    if (!d) return []
    return buildPersonalLandingReconfigureSections({
      data: { ...d, introText, serviceTags },
      currentSections: landingSections,
      purposeId: reconfigurePurpose,
      features: reconfigureFeatures,
    })
  }, [d, introText, landingSections, reconfigureFeatures, reconfigurePurpose, serviceTags])

  const reconfigureWritingItems = useMemo(
    () => getPersonalWritingItems(reconfigurePreviewSections),
    [reconfigurePreviewSections],
  )

  const reconfigureSelectedFeatureLabels = useMemo(
    () => PERSONAL_LANDING_RECONFIGURE_FEATURES
      .filter((feature) => feature.required || reconfigureFeatures[feature.id] === true)
      .map((feature) => feature.label),
    [reconfigureFeatures],
  )

  const getPersonalLandingComplianceText = useCallback(() => {
    const sectionBlob = collectMarketingStrings(landingSections, 3200)
    const lines = [
      bioShort,
      introText,
      careerText,
      activityAreaText,
      freeText,
      companyNameOverride,
      jobTitle,
      serviceTags,
      addressText,
      mobileFaxNumber,
      sectionBlob,
    ]
    return lines
      .filter((s) => String(s || '').trim())
      .join('\n')
      .slice(0, 4000)
  }, [
    bioShort,
    introText,
    careerText,
    activityAreaText,
    freeText,
    companyNameOverride,
    jobTitle,
    serviceTags,
    addressText,
    mobileFaxNumber,
    landingSections,
  ])

  const landingDraftForPreview = useMemo(() => {
    if (!d) return null
    const galleryFileIds = (Array.isArray(landingGalleryItems) ? landingGalleryItems : [])
      .map((it) => it?.fileId)
      .filter(Boolean)
    return {
      ...d,
      title: jobTitle || d.title,
      cardEnabled: true,
      landingEnabled: true,
      showLandingLink,
      bioShort,
      introText,
      careerText,
      activityAreaText,
      freeText,
      kakaoChatUrl,
      profileImageUrl,
      profileImageFileId,
      showCareerCard,
      showActivityAreaCard,
      showFreeTextCard,
      imageCardEnabled,
      imageCardUrl,
      imageCardFileId,
      landingGalleryEnabled,
      landingGalleryUrls: galleryFileIds,
      companyNameOverride,
      jobTitle,
      addressText,
      mobileFaxNumber,
      serviceTags,
      youtubeUrl,
      instagramUrl,
      blogUrl,
      landingSections,
      landingTemplateId,
      landingPageStyle,
    }
  }, [
    d,
    showLandingLink,
    bioShort,
    introText,
    careerText,
    activityAreaText,
    freeText,
    kakaoChatUrl,
    profileImageUrl,
    profileImageFileId,
    showCareerCard,
    showActivityAreaCard,
    showFreeTextCard,
    imageCardEnabled,
    imageCardUrl,
    imageCardFileId,
    landingGalleryEnabled,
    landingGalleryItems,
    companyNameOverride,
    jobTitle,
    addressText,
    mobileFaxNumber,
    serviceTags,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    landingSections,
    landingTemplateId,
    landingPageStyle,
  ])

  const publicLandingStateHeaderProps = {
    title: '개인홈페이지',
    titleAccessory: <MenuGuideButton slug="public-landing" title="개인홈페이지 가이드" />,
  }

  if (myQuery.isLoading) return (
    <EditorStatePage {...publicLandingStateHeaderProps}>
      <div className="rounded-2xl border border-sgez-border-strong bg-sgez-surface shadow-sm">
        <Loading />
      </div>
    </EditorStatePage>
  )
  if (myQuery.isError) return (
    <EditorStatePage {...publicLandingStateHeaderProps}>
      <div className="rounded-2xl border border-sgez-border-strong bg-sgez-surface shadow-sm">
        <InlineError message={myQuery.error?.message || '불러오기 실패'} />
      </div>
    </EditorStatePage>
  )
  if (!d) return (
    <EditorStatePage {...publicLandingStateHeaderProps}>
      <div className="rounded-2xl border border-sgez-border-strong bg-sgez-surface shadow-sm">
        <InlineError message="데이터가 없습니다." />
      </div>
    </EditorStatePage>
  )

  const canShare = Boolean(code && absoluteShareUrl && absoluteLandingUrl)
  const updateSectionContent = (index, updater) => {
    setLandingSections((prev) => prev.map((section, i) => {
      if (i !== index) return section
      const nextContent = typeof updater === 'function'
        ? updater(section?.content || {})
        : updater
      return { ...section, content: nextContent && typeof nextContent === 'object' ? nextContent : {} }
    }))
  }

  const applyDbTemplate = (t) => {
    const preset = t?.preset && typeof t.preset === 'object' ? t.preset : {}
    const nextSkin = typeof preset.landingTemplateId === 'string' && preset.landingTemplateId ? preset.landingTemplateId : 'LANDING_V1'
    setLandingTemplateId(nextSkin)
    setLandingSections(normalizePersonalLandingSections(preset, { ...d, introText, serviceTags }, preset?.landingPageStyle))
    if (preset?.landingPageStyle && typeof preset.landingPageStyle === 'object') {
      setLandingPageStyle({ ...preset.landingPageStyle })
    } else {
      setLandingPageStyle({})
    }
    setExpandedSectionId('')
    try { window?.toast?.show?.(`「${t?.name || t?.code}」템플릿을 불러왔습니다. 저장하면 반영됩니다.`, 'success') } catch { }
  }

  const openReconfigure = () => {
    const purpose = getPersonalLandingPurposeFromSections(landingSections)
    setReconfigurePurpose(purpose)
    setReconfigureFeatures(getDefaultPersonalLandingFeatureValues(landingSections, purpose))
    setReconfigureOpen(true)
  }

  const applyReconfigure = () => {
    const nextSections = reconfigurePreviewSections
    setLandingSections(nextSections)
    setExpandedSectionId(nextSections[0]?.id || '')
    setMobileEditorTab('edit')
    setLandingEditorMode('guided')
    setGuidedStart((prev) => ({ key: prev.key + 1, phase: 3, index: 0 }))
    setInitialSetupDone(true)
    setReconfigureOpen(false)
    try { window?.toast?.show?.('개인홈페이지 구성을 적용했습니다. 저장하면 고객에게 반영됩니다.', 'success') } catch { }
  }

  const reconfigureContentProps = {
    title: '개인홈페이지 다시 구성하기',
    description: '기존 연락처와 입력한 문구는 유지하고, 보여줄 기능과 섹션 순서를 다시 정리합니다.',
    purposeLabel: '방문자가 무엇을 하길 원하나요?',
    purposes: PERSONAL_LANDING_RECONFIGURE_PURPOSES,
    purposeId: reconfigurePurpose,
    onPurposeIdChange: (value) => {
      setReconfigurePurpose(value)
      setReconfigureFeatures(getDefaultPersonalLandingFeatureValues(landingSections, value))
    },
    features: PERSONAL_LANDING_RECONFIGURE_FEATURES,
    featureValues: reconfigureFeatures,
    onFeatureChange: (id, value) => setReconfigureFeatures((prev) => ({ ...prev, [id]: value })),
    onApply: applyReconfigure,
    applyLabel: '작성 시작',
    note: '적용 후 미리보기에서 확인할 수 있습니다. 고객에게 반영하려면 저장을 눌러야 합니다.',
    stepMode: true,
    stepLabels: ['목적 선택', '기능 선택', '작성 항목'],
    renderReview: () => (
      <section>
        <div className="text-sm font-bold text-slate-900">이 순서로 작성합니다</div>
        <div className="mt-3 overflow-hidden rounded-3xl border border-slate-200 bg-white">
          {reconfigureWritingItems.map((item, index) => (
            <div
              key={item.id}
              className={`flex gap-3 px-4 py-4 ${index === 0 ? '' : 'border-t border-slate-100'}`}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-xs font-black text-brand-navy">
                {index + 1}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-extrabold text-slate-950">{item.label}</div>
                <div className="mt-1 text-xs leading-5 text-slate-500">{item.description}</div>
              </div>
            </div>
          ))}
        </div>
        {reconfigureSelectedFeatureLabels.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {reconfigureSelectedFeatureLabels.map((label) => (
              <span
                key={label}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700"
              >
                {label}
              </span>
            ))}
          </div>
        ) : null}
      </section>
    ),
  }

  const reconfigureModal = (
    <EasyReconfigureModal
      open={reconfigureOpen}
      {...reconfigureContentProps}
      onClose={() => setReconfigureOpen(false)}
    />
  )

  if (!initialSetupDone && !isPersonalLandingConfigured(d)) {
    return (
      <EditorStatePage {...publicLandingStateHeaderProps}>
        <div className="mx-auto max-w-3xl py-5">
          <EasyReconfigureContent
            {...reconfigureContentProps}
            title="생성된 개인홈페이지가 없습니다. 만들어주세요."
            description="질문에 답하면 필요한 섹션이 먼저 배치되고, 이후 세부 내용을 편집할 수 있습니다."
            showCloseButton={false}
            className="shadow-sm"
          />
        </div>
      </EditorStatePage>
    )
  }

  const editorFullCtx = {
    d,
    bioShort, setBioShort,
    introText, setIntroText,
    careerText, setCareerText,
    activityAreaText, setActivityAreaText,
    freeText, setFreeText,
    kakaoChatUrl, setKakaoChatUrl,
    profileImageUrl, setProfileImageUrl,
    profileImageFileId, setProfileImageFileId,
    showCareerCard, setShowCareerCard,
    showActivityAreaCard, setShowActivityAreaCard,
    showFreeTextCard, setShowFreeTextCard,
    imageCardEnabled, setImageCardEnabled,
    imageCardUrl, setImageCardUrl,
    imageCardFileId, setImageCardFileId,
    landingGalleryEnabled, setLandingGalleryEnabled,
    landingGalleryItems, setLandingGalleryItems,
    companyNameOverride, setCompanyNameOverride,
    jobTitle, setJobTitle,
    addressText, setAddressText,
    mobileFaxNumber, setMobileFaxNumber,
    serviceTags, setServiceTags,
    youtubeUrl, setYoutubeUrl,
    instagramUrl, setInstagramUrl,
    blogUrl, setBlogUrl,
    uploadProfileImageMutation,
    uploadImageCardMutation,
    uploadLandingGalleryMutation,
    updateMutation,
  }

  return (
    <div className="min-h-full bg-sgez-surface">
      <header className="sgez-editor-header sticky top-0 z-30 border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-[min(100%,90rem)] flex-col gap-3 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-bold text-slate-900">개인홈페이지 편집</h1>
              <MenuGuideButton slug="public-landing" title="개인홈페이지 가이드" />
            </div>
            <div className="flex min-w-0 max-w-full flex-[1_1_16rem] flex-wrap items-center justify-end gap-1.5 sm:gap-2">
              <div className="grid min-w-0 w-full max-w-md flex-[1_1_280px] grid-cols-4 gap-1 sm:w-auto sm:max-w-none sm:flex sm:flex-nowrap">
                <a
                  className={`${shareActionBtnClass} ${canShare ? '' : 'cursor-not-allowed opacity-40'}`}
                  href={canShare ? landingUrlPath : undefined}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => { if (!canShare) e.preventDefault() }}
                >
                  미리보기
                </a>
                <button
                  type="button"
                  className={shareActionBtnClass}
                  disabled={!canShare}
                  title="카카오·SNS 미리보기용 OG 링크"
                  onClick={() => {
                    if (canShare) {
                      copyToClipboard(absoluteShareUrl, 'OG 링크가 복사되었습니다.')
                    }
                  }}
                >
                  링크복사
                </button>
                {/* <button
                  type="button"
                  className={`${shareActionBtnClass} ${canShare ? '' : 'cursor-not-allowed opacity-40'}`}
                  disabled={!canShare}
                  onClick={async () => {
                    if (!canShare) return
                    try {
                      initKakao()
                      const Kakao = window.Kakao
                      if (!Kakao?.Share?.sendScrap) throw new Error('kakao_share_not_supported')
                      Kakao.Share.sendScrap({ requestUrl: absoluteShareUrl })
                    } catch {
                      await copyToClipboard(absoluteShareUrl, 'OG 링크가 복사되었습니다.')
                    }
                  }}
                >
                  카카오톡
                </button>
                <a
                  className={`${shareActionBtnClass} ${smsShareHref ? '' : 'cursor-not-allowed opacity-40'}`}
                  href={smsShareHref || undefined}
                  onClick={(e) => { if (!smsShareHref) e.preventDefault() }}
                >
                  문자
                </a> */}
              </div>
              <button
                type="button"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={() => navigate(-1)}
              >
                돌아가기
              </button>
              <button
                type="button"
                className="rounded-xl bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                disabled={updateMutation.isLoading}
                onClick={() => setSaveReviewOpen(true)}
              >
                {updateMutation.isLoading ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
          {!canShare ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
              공유 링크가 아직 없습니다. 잠시 후 새로고침해 보세요.
            </p>
          ) : null}
        </div>
      </header>

      <div className="mx-auto max-w-[min(100%,90rem)] px-4 py-6">
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="text-sm font-bold text-slate-900">편집 방식</div>
              <div className="mt-1 text-xs leading-5 text-slate-500">
                처음부터 다시 구성하거나 모든 섹션을 한 번에 편집할 수 있습니다.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0 sm:flex-wrap sm:justify-end">
              <button
                type="button"
                className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-bold text-orange-800 hover:bg-orange-100"
                onClick={openReconfigure}
              >
                다시 처음부터 구성하기
              </button>
              <button
                type="button"
                className={`rounded-xl border px-3 py-2 text-xs font-bold ${
                  landingEditorMode === 'advanced'
                    ? 'border-brand-navy bg-sky-50 text-brand-navy'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
                onClick={() => setLandingEditorMode('advanced')}
              >
                고급편집
              </button>
              {landingEditorMode === 'advanced' ? (
                <button
                  type="button"
                  className="col-span-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 sm:col-span-1"
                  onClick={() => {
                    setLandingEditorMode('guided')
                    setGuidedStart((prev) => ({ key: prev.key + 1, phase: 1, index: 0 }))
                  }}
                >
                  STEP 편집
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mb-4 flex rounded-xl border border-slate-200 bg-white p-1 lg:hidden">
          <button
            type="button"
            className={`min-h-[40px] flex-1 rounded-lg px-3 py-2 text-sm font-semibold ${mobileEditorTab === 'preview' ? 'bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setMobileEditorTab('preview')}
          >
            실시간 미리보기
          </button>
          <button
            type="button"
            className={`min-h-[40px] flex-1 rounded-lg px-3 py-2 text-sm font-semibold ${mobileEditorTab === 'edit' ? 'bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setMobileEditorTab('edit')}
          >
            편집
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[7fr_3fr] lg:items-start">
          <section
            className={`min-w-0 space-y-2 ${mobileEditorTab === 'edit' ? 'hidden' : ''} lg:block`}
            aria-label="실시간 미리보기"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">실시간 미리보기</div>
              <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-[11px] font-semibold transition ${previewDeviceMode === 'pc' ? 'bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => setPreviewDeviceMode('pc')}
                >
                  PC
                </button>
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-[11px] font-semibold transition ${previewDeviceMode === 'mobile' ? 'bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => setPreviewDeviceMode('mobile')}
                >
                  모바일
                </button>
              </div>
            </div>
            <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-5.5rem)] overflow-y-auto rounded-2xl border border-slate-200/90 bg-slate-100/90 p-2 shadow-inner ring-1 ring-black/5">
              {landingDraftForPreview ? (
                <PersonalLandingPreviewViewport mode={previewDeviceMode}>
                  <PersonalLandingDraftPreview data={landingDraftForPreview} />
                </PersonalLandingPreviewViewport>
              ) : (
                <div className="flex min-h-[200px] items-center justify-center text-sm text-slate-500">미리보기를 준비하는 중입니다.</div>
              )}
            </div>
          </section>

          <section
            className={`min-w-0 space-y-5 ${mobileEditorTab === 'preview' ? 'hidden' : ''} lg:block`}
            aria-label="편집"
          >
        <PersonalLandingSectionsEditor
          mode="full"
          landingSections={landingSections}
          setLandingSections={setLandingSections}
          expandedSectionId={expandedSectionId}
          setExpandedSectionId={setExpandedSectionId}
          updateSectionContent={updateSectionContent}
          full={editorFullCtx}
          presetData={{ ...d, introText, serviceTags }}
          dbTemplates={dbTemplatesActive}
          onApplyDbTemplate={applyDbTemplate}
          guidedFlow={landingEditorMode !== 'advanced'}
          guidedStartPhase={guidedStart.phase}
          guidedStartIndex={guidedStart.index}
          guidedStartKey={guidedStart.key}
        />

        {/* <details className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <summary className="cursor-pointer list-none text-sm font-semibold text-slate-800 [&::-webkit-details-marker]:hidden">
            페이지 외형·연결
          </summary>
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={showLandingLink} onChange={(e) => setShowLandingLink(e.target.checked)} />
                명함에 홈페이지 링크
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
              <span className="text-xs font-semibold text-slate-500">페이지 스킨</span>
              <select
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                value={landingTemplateId}
                onChange={(e) => setLandingTemplateId(e.target.value)}
              >
                {LANDING_SKIN_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
              <span className="text-xs font-semibold text-slate-500">강조 색</span>
              <input
                type="color"
                aria-label="강조 색"
                className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
                value={typeof landingPageStyle.primaryColor === 'string' && /^#[0-9A-Fa-f]{6}$/.test(landingPageStyle.primaryColor) ? landingPageStyle.primaryColor : '#1e3a8a'}
                onChange={(e) => setLandingPageStyle((s) => ({ ...s, primaryColor: e.target.value }))}
              />
              <button
                type="button"
                className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                onClick={() => setLandingPageStyle((s) => {
                  const next = { ...s }
                  delete next.primaryColor
                  return next
                })}
              >
                기본(네이비)
              </button>
            </div>
          </div>
        </details> */}

      </section>
        </div>
      </div>

      {reconfigureModal}

      <PersonalLandingSaveReviewModal
        open={saveReviewOpen}
        onClose={() => setSaveReviewOpen(false)}
        getComplianceText={getPersonalLandingComplianceText}
        onConfirmSave={() => {
          updateMutation.mutate(undefined, {
            onSuccess: () => { setSaveReviewOpen(false) },
          })
        }}
        saving={updateMutation.isLoading}
      />
    </div>
  )
}
